import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';

export let WS_ENDPOINT: string;

export interface TTSStreamMessage {
    type: 'chunk' | 'complete' | 'error' | 'voices';
    data?: Blob;
    voices?: any[];
    error?: string;
}

@Injectable({
    providedIn: 'root',
})
export class TTSApiService {
    private socket!: WebSocketSubject<any> | undefined;
    private connectionSubject = new BehaviorSubject<WebSocketSubject<any> | null>(null);
    private streamSubject = new Subject<TTSStreamMessage>();
    private audioChunks: Blob[] = [];
    private isCollectingAudio = false;

    constructor() {
        WS_ENDPOINT = `${environment.API_URL}`;
    }

    connect(): void {
        console.log('[TTSApiService]: connect() called. Socket exists:', !!this.socket, 'Socket closed:', this.socket?.closed);
        if (!this.socket || this.socket?.closed) {
            this.socket = this.getNewSocket();
            this.connectionSubject.next(this.socket);
        } else {
            console.log('[TTSApiService]: Reusing existing socket connection');
        }
    }

    sendMessage(msg: any): void {
        // Reset state for new request
        this.audioChunks = [];
        this.isCollectingAudio = false;
        this.socket?.next(msg);
    }

    close(): void {
        this.socket?.complete();
        this.socket = undefined;
    }

    getConnection(): BehaviorSubject<WebSocketSubject<any> | null> {
        return this.connectionSubject;
    }

    getStream(): Subject<TTSStreamMessage> {
        return this.streamSubject;
    }

    private getNewSocket() {
        console.log('[TTSApiService]: Attempting WebSocket connection to:', WS_ENDPOINT);
        const socket = webSocket({
            url: WS_ENDPOINT,
            openObserver: {
                next: (event: any) => {
                    console.log('[TTSApiService]: WebSocket connection opened successfully');
                },
            },
            closeObserver: {
                next: (event: any) => {
                    console.log('[TTSApiService]: WebSocket connection closed. Code:', event.code, 'Reason:', event.reason, 'Was clean:', event.wasClean);
                    this.socket = undefined;
                    this.connectionSubject.next(null);
                    // If we were collecting audio, emit complete
                    if (this.isCollectingAudio) {
                        console.log('[TTSApiService]: Connection closed while collecting audio, emitting complete');
                        this.emitComplete();
                    }
                },
            },
            serializer: (msg: any) => {
                console.log('[TTSApiService]: Sending message:', typeof msg === 'string' ? msg : JSON.stringify(msg).substring(0, 200));
                if (msg instanceof Blob) {
                    return msg;
                }
                return JSON.stringify(msg);
            },
            deserializer: (response: any) => {
                console.log('[TTSApiService]: Raw response received. Event type:', response?.type, 'Data type:', typeof response?.data);
                return response.data;
            },
        });

        // Subscribe to socket messages to handle them
        socket.subscribe({
            next: (data: any) => {
                this.handleMessage(data);
            },
            error: (error: any) => {
                console.error('[TTSApiService]: Socket error:', error);
                this.streamSubject.next({
                    type: 'error',
                    error: error?.message || 'WebSocket connection error',
                });
            },
            complete: () => {
                console.log('[TTSApiService]: Socket complete');
                if (this.isCollectingAudio) {
                    this.emitComplete();
                }
            },
        });

        return socket;
    }

    private handleMessage(data: any): void {
        console.log('[TTSApiService]: handleMessage called. Data type:', typeof data, 'Instance:', data?.constructor?.name, 'Data:', data);
        // Check if data is binary (Blob or ArrayBuffer)
        if (data instanceof Blob) {
            console.log('[TTSApiService]: Received Blob chunk. Size:', data.size, 'Type:', data.type);
            this.audioChunks.push(data);
            this.isCollectingAudio = true;
            this.streamSubject.next({
                type: 'chunk',
                data: data,
            });
        } else if (data instanceof ArrayBuffer) {
            console.log('[TTSApiService]: Received ArrayBuffer chunk. Size:', data.byteLength);
            const blob = new Blob([data]);
            this.audioChunks.push(blob);
            this.isCollectingAudio = true;
            this.streamSubject.next({
                type: 'chunk',
                data: blob,
            });
        } else if (typeof data === 'string') {
            console.log('[TTSApiService]: Received string data:', data.substring(0, 200));
            // Try to parse as JSON
            try {
                const parsed = JSON.parse(data);
                console.log('[TTSApiService]: Parsed JSON:', parsed);
                this.handleJsonMessage(parsed);
            } catch (e) {
                // Not JSON, treat as error or ignore
                console.warn('[TTSApiService]: Received non-JSON string:', data);
            }
        } else {
            // Already parsed JSON object
            console.log('[TTSApiService]: Received object data:', data);
            this.handleJsonMessage(data);
        }
    }

    private handleJsonMessage(data: any): void {
        console.log('[TTSApiService]: handleJsonMessage called. Has error:', !!data?.error, 'Has voices:', !!data?.voices, 'Keys:', Object.keys(data || {}));
        if (data?.error) {
            console.error('[TTSApiService]: Error from server:', data.error);
            this.streamSubject.next({
                type: 'error',
                error: data.error,
            });
        } else if (data?.voices) {
            console.log('[TTSApiService]: Received voices list. Count:', data.voices.length);
            this.streamSubject.next({
                type: 'voices',
                voices: data.voices,
            });
        } else {
            // Other JSON messages - could be metadata or end signal
            // Check if this indicates end of stream
            console.log('[TTSApiService]: Checking for end/complete signal. end:', data?.end, 'complete:', data?.complete);
            if (data?.end || data?.complete) {
                console.log('[TTSApiService]: End signal received, emitting complete');
                this.emitComplete();
            }
        }
    }

    private emitComplete(): void {
        console.log('[TTSApiService]: emitComplete called. Audio chunks count:', this.audioChunks.length);
        if (this.audioChunks.length > 0) {
            const completeBlob = new Blob(this.audioChunks, { type: 'audio/wav' });
            console.log('[TTSApiService]: Emitting complete blob. Size:', completeBlob.size);
            this.streamSubject.next({
                type: 'complete',
                data: completeBlob,
            });
        } else {
            console.warn('[TTSApiService]: emitComplete called but no audio chunks available');
        }
        this.audioChunks = [];
        this.isCollectingAudio = false;
    }
}
